import type { CourseSessionAttendee } from '../types/crm';

export type SessionAttendeeStatus = CourseSessionAttendee['status'];

export function isSafePassCourseName(courseName: string | undefined | null): boolean {
  const normalized = String(courseName || '').toLowerCase();
  return normalized.includes('safe pass') || normalized.includes('safepass');
}

export type CertificateEligibilityOptions = {
  courseName?: string | null;
  processNumber?: string;
  cardStatus?: string;
};

/** Normalize raw DB status (null → booked). */
export function normalizedAttendeeStatus(
  status: SessionAttendeeStatus | string | null | undefined
): SessionAttendeeStatus {
  return (status || 'booked') as SessionAttendeeStatus;
}

/** Reasons this attendee cannot receive a certificate (empty = eligible). */
export function getCertificateIneligibilityReasons(
  attendee: CourseSessionAttendee,
  options?: CertificateEligibilityOptions
): string[] {
  const reasons: string[] = [];
  // Safe Pass: SOLAS plastic cards only — never issue RapidCert standard certificates.
  if (isSafePassCourseName(options?.courseName)) {
    reasons.push('Safe Pass courses do not issue standard certificates');
  }
  if (attendee.isVirtual) reasons.push('Virtual seat');
  if (!attendee.individualCustomerId) reasons.push('No individual participant');
  if (isAttendeeNoShow(attendee)) reasons.push('Marked no-show');
  if (isAttendeeFailed(attendee)) reasons.push('Marked failed');
  if (!isAttendeeOnActiveRoster(attendee)) reasons.push('Not on active roster');
  if (!attendee.sendCertificate) reasons.push('Send Certificate not ticked');
  return reasons;
}

export function isAttendeeCertificateEligible(
  attendee: CourseSessionAttendee,
  options?: CertificateEligibilityOptions
): boolean {
  return getCertificateIneligibilityReasons(attendee, options).length === 0;
}

export function assertAttendeeCertificateEligible(
  attendee: CourseSessionAttendee,
  options?: CertificateEligibilityOptions
): void {
  const reasons = getCertificateIneligibilityReasons(attendee, options);
  if (reasons.length > 0) {
    throw new Error(`Certificate not allowed: ${reasons.join(', ')}`);
  }
}

/** On the active roster (counts toward session headcount). */
export function isAttendeeOnActiveRoster(attendee: CourseSessionAttendee): boolean {
  const status = normalizedAttendeeStatus(attendee.status);
  return status === 'booked' || status === 'completed';
}

/** Company-invoice settlement cohort and session financial seat counts. */
export function isAttendeeCountedInSessionFinancials(
  attendee: Pick<CourseSessionAttendee, 'status'>
): boolean {
  return isAttendeeOnActiveRoster(attendee as CourseSessionAttendee);
}

/** Total fees, outstanding, overpayment, and payment-mix denominators. */
export function isAttendeeInFeeTotals(
  attendee: Pick<CourseSessionAttendee, 'status'>
): boolean {
  return isAttendeeOnActiveRoster(attendee as CourseSessionAttendee);
}

/** Booked + attended seats included in session headcount (calendar, roster cards). */
export function isAttendeeCountedInBookingTotal(
  attendee: Pick<CourseSessionAttendee, 'status' | 'isVirtual'>
): boolean {
  return isAttendeeOnActiveRoster(attendee as CourseSessionAttendee);
}

/** Blocks adding the same individual again (active roster, no-show, or failed). */
export function isAttendeeBlockingReEnrollment(
  attendee: Pick<CourseSessionAttendee, 'status'>
): boolean {
  const status = normalizedAttendeeStatus(attendee.status);
  return status === 'booked' || status === 'completed' || status === 'no_show' || status === 'failed';
}

export function findConflictingEnrollment(
  attendees: Array<Pick<CourseSessionAttendee, 'id' | 'status' | 'individualCustomerId'>>,
  individualCustomerId: string,
  excludeAttendeeId?: string
): Pick<CourseSessionAttendee, 'id' | 'status'> | undefined {
  return attendees.find(
    (row) =>
      row.individualCustomerId === individualCustomerId &&
      row.id !== excludeAttendeeId &&
      isAttendeeBlockingReEnrollment(row)
  );
}

export function conflictMessageForEnrollment(
  conflict: Pick<CourseSessionAttendee, 'status'> | undefined
): string {
  if (!conflict) return 'This person is already booked on this session.';
  if (isAttendeeNoShow(conflict as CourseSessionAttendee)) {
    return 'This person is already on this session (marked no-show).';
  }
  if (isAttendeeFailed(conflict as CourseSessionAttendee)) {
    return 'This person is already on this session (marked failed).';
  }
  return 'This person is already booked on this session.';
}

/** Waiting list — own queue, does not use a roster seat. */
export function isAttendeeWaitingList(attendee: CourseSessionAttendee): boolean {
  if (attendee.status === 'waiting_list') return true;
  if (attendee.status === 'cancelled' && /waiting list/i.test(String(attendee.attendeeNotes || ''))) {
    return true;
  }
  return false;
}

export function isAttendeeRemoved(attendee: CourseSessionAttendee): boolean {
  return attendee.status === 'cancelled' && !isAttendeeWaitingList(attendee);
}

export function isAttendeeNoShow(attendee: CourseSessionAttendee): boolean {
  return attendee.status === 'no_show';
}

export function isAttendeeFailed(attendee: CourseSessionAttendee): boolean {
  return attendee.status === 'failed';
}

export function attendeeStatusLabel(status: SessionAttendeeStatus | string | undefined): string {
  switch (status) {
    case 'booked':
      return 'Booked';
    case 'completed':
      return 'Attended';
    case 'waiting_list':
      return 'Waiting list';
    case 'no_show':
      return 'No-show';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Removed';
    default:
      return String(status || 'Unknown');
  }
}

/** Raw attendee row shape from course_session_attendees (snake_case). */
export type InstructorPortalAttendeeRow = {
  status?: string | null;
  is_virtual?: boolean | null;
  placeholder_label?: string | null;
  enrollment_first_name?: string | null;
  enrollment_surname?: string | null;
  individual_customer_id?: string | null;
  company_customer_id?: string | null;
};

/**
 * Build human-readable attendee lines for the instructor portal.
 * Groups unassigned company reserved spots instead of repeating the company name per seat.
 */
export function formatInstructorAttendeeDisplayLines(
  rows: InstructorPortalAttendeeRow[],
  customerNameById: Map<string, string>
): string[] {
  const named: string[] = [];
  const virtualByLabel = new Map<string, number>();

  for (const row of rows) {
    if ((row.status || 'booked') === 'cancelled') continue;

    if (row.is_virtual) {
      const label = String(row.placeholder_label || '').trim() || 'Reserved spot';
      virtualByLabel.set(label, (virtualByLabel.get(label) || 0) + 1);
      continue;
    }

    const enrollmentName = `${row.enrollment_first_name || ''} ${row.enrollment_surname || ''}`.trim();
    const customerName =
      customerNameById.get(String(row.individual_customer_id || row.company_customer_id || '')) || '';
    const name = enrollmentName || customerName;
    named.push(name || 'Unknown attendee');
  }

  const virtualSummaries = Array.from(virtualByLabel.entries()).map(([label, count]) => {
    if (count === 1) {
      return `${label} (1 reserved spot — name not assigned yet)`;
    }
    return `${label} (${count} reserved spots — names not assigned yet)`;
  });

  return [...named, ...virtualSummaries];
}